import { IColumn, IDetailsRowProps, IRenderFunction, SelectionMode, Stack } from "@fluentui/react";
import React, { ReactElement, ReactNode, useEffect, useState } from "react";

import { Text } from "../items/Text";
import { LayoutMode } from "./LayoutMode";
import { ListTableColumn } from "./ListTableColumn";
import { ListTablePagination } from "./ListTablePagination";
import { ListTableSort, sortColumns } from "./ListTableSort";
import { renderDefault } from "./renderFunctions";
import { ShimmeredDetailsList } from "./ShimmeredDetailsList";

export type DetailsRowProps = IDetailsRowProps;
export type RenderFunction<T> = IRenderFunction<T>;

export interface ListTableProps<T> {
    columns: ListTableColumn<T>[];
    items: T[];
    isLoading: boolean;
    noItemsMessage?: string;
    pagination?: PaginationOptions;
    selectionMode?: SelectionMode;
    selectedItems?: T[];
    sort?: ListTableSort;
    layoutMode?: LayoutMode;
    shimmerLines?: number;
    maintainSelection?: boolean;
    onRenderRow?: RenderFunction<DetailsRowProps>;
    onRenderExpandedRow?: RenderFunction<DetailsRowProps>;
    onSortChange?(sort: ListTableSort): void;
    onSelectionChange?(selectedItems: T[]): void;
    isItemSelectable?(item: T): boolean;
    testId?: string;
    isStickyModeEnabled?: boolean;
}

export interface PaginationOptions {
    page: number;
    pageSize: number;
    totalItems: number;
    onPageChange(page: number): void;
    onPageSizeChange?(pageSize: number): void;
}

export const ListTable = <T,>({
    columns,
    items,
    isLoading,
    selectedItems,
    noItemsMessage,
    pagination,
    sort,
    layoutMode,
    shimmerLines,
    maintainSelection,
    onRenderRow,
    onRenderExpandedRow,
    onSortChange,
    onSelectionChange,
    isItemSelectable,
    selectionMode,
    testId,
    isStickyModeEnabled
}: ListTableProps<T>): ReactElement | null => {
    const [sortedColumns, setSortedColumns] = useState<ListTableColumn<T>[]>(columns);

    useEffect(() => {
        const newColumns = sortColumns(columns, sort);
        setSortedColumns(newColumns);
    }, [sort, columns]);

    const onPageChange = (pageNumber: number) => pagination?.onPageChange(pageNumber);
    const onPageSizeChange = pagination?.onPageSizeChange
        ? (pageSize: number) => pagination?.onPageSizeChange?.(pageSize)
        : undefined;

    const onRenderItemColumn = (item?: unknown, _index?: number | undefined, column?: IColumn): ReactNode => {
        return renderDefault(item, _index, column);
    };

    const onColumnHeaderClick = (e?: React.MouseEvent<HTMLElement>, column?: ListTableColumn<unknown>) => {
        e?.preventDefault();

        if (column && column.isSortable && onSortChange) {
            if (column.isSorted && !column.isSortedDescending) {
                onSortChange({ by: column.key, order: "desc" });
            } else {
                onSortChange({ by: column.key, order: "asc" });
            }
        }
    };

    if (!items.length && !isLoading) {
        return (
            <Stack horizontalAlign="center" data-testid="table-no-data" style={{ paddingTop: 20 }}>
                <Text>{noItemsMessage || "No data available"}</Text>
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 15 }} data-testid={testId} data-testisloading={isLoading}>
            <Stack styles={{ root: { overflowX: "auto" } }} tokens={{ childrenGap: 5 }}>
                <ShimmeredDetailsList
                    columns={sortedColumns}
                    items={items}
                    selectedItems={selectedItems}
                    layoutMode={layoutMode}
                    enableShimmer={isLoading}
                    shimmerLines={pagination?.pageSize ?? shimmerLines}
                    onRenderItemColumn={onRenderItemColumn}
                    onColumnHeaderClick={onColumnHeaderClick}
                    onRenderRow={onRenderRow}
                    onRenderExpandedRow={onRenderExpandedRow}
                    onSelectionChange={onSelectionChange}
                    isItemSelectable={isItemSelectable}
                    onShouldVirtualize={() => false}
                    selectionMode={selectionMode}
                    maintainSelection={maintainSelection}
                    testId={testId}
                    isStickyModeEnabled={isStickyModeEnabled}
                />
            </Stack>
            {pagination && (
                <ListTablePagination
                    currentPage={pagination.page}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    totalItemsCount={pagination.totalItems}
                    pageSize={pagination.pageSize}
                />
            )}
        </Stack>
    );
};
